import { getContent } from '../services/content';
import { escapeHtml, sanitizeUrl } from './security';
import type { Content } from '../types';

/**
 * Generates the HTML string for the CV export.
 *
 * @param data The portfolio content data.
 * @returns The generated HTML string.
 */
export function generateCVHtml(data: Content): string {
    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>${escapeHtml(data.hero.name)} - CV</title>
    <style>
        @page {
            margin: 2cm;
            size: A4;
        }
        body {
            font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
            color: #222;
            line-height: 1.5;
            margin: 0;
            padding: 0;
            font-size: 10pt;
        }
        h1, h2, h3, h4 {
            color: #111;
            margin-bottom: 0.5em;
            font-weight: 600;
        }
        h1 { font-size: 24pt; margin-top: 0; border-bottom: 2px solid #222; padding-bottom: 8px; margin-bottom: 8px; }
        h2 { font-size: 14pt; margin-top: 1.5em; border-bottom: 1px solid #ccc; padding-bottom: 4px; text-transform: uppercase; letter-spacing: 1px; }
        h3 { font-size: 11pt; margin-top: 1em; margin-bottom: 0.2em; }
        p { margin-top: 0; margin-bottom: 1em; }
        ul { margin-top: 0.5em; padding-left: 20px; margin-bottom: 1em; }
        li { margin-bottom: 0.3em; }
        a { color: #0056b3; text-decoration: none; }
        
        .contact-info { margin-bottom: 1.5em; font-size: 10pt; }
        .contact-info a { margin-right: 15px; }
        
        .section-content { margin-top: 1em; }
        
        .item-block { margin-bottom: 1.2em; page-break-inside: avoid; }
        .item-header { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 0.2em; }
        .item-title { font-weight: bold; font-size: 11pt; }
        .item-meta { color: #555; font-size: 9.5pt; font-style: italic; }
        .item-subtitle { font-weight: 500; font-size: 10pt; margin-bottom: 0.5em; color: #333; }
        .tech-stack { font-size: 9pt; color: #666; margin-top: 0.5em; }
        
        .skills-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1em; }
        .skill-group { page-break-inside: avoid; }
        .skill-group h4 { margin-top: 0; margin-bottom: 0.2em; font-size: 10pt; }
        
        @media screen {
            body { padding: 2cm; max-width: 21cm; margin: 0 auto; background: white; box-shadow: 0 0 10px rgba(0,0,0,0.1); margin-top: 2cm; margin-bottom: 2cm; }
            html { background: #eee; }
        }
    </style>
</head>
<body>
    <h1>${escapeHtml(data.hero.name)}</h1>
    <div style="font-size: 12pt; color: #444; margin-bottom: 10px;">${escapeHtml(data.hero.title)}</div>
    
    <div class="contact-info">
        ${data.contact.methods.map(m => `<a href="${sanitizeUrl(m.url)}" target="_blank">${escapeHtml(m.label)}: ${escapeHtml(m.url.replace('mailto:', '').replace('https://', ''))}</a>`).join('')}
    </div>

    <h2>${escapeHtml(data.about.heading)}</h2>
    <div class="section-content">
        ${data.about.paragraphs.map(p => `<p>${escapeHtml(p)}</p>`).join('')}
    </div>

    <h2>${escapeHtml(data.experience.heading)}</h2>
    <div class="section-content">
        ${data.experience.items.map(item => `
            <div class="item-block">
                <div class="item-header">
                    <span class="item-title">${escapeHtml(item.roleTitle)}</span>
                    <span class="item-meta">${escapeHtml(item.period)} | ${escapeHtml(item.location)}</span>
                </div>
                <div class="item-subtitle">${escapeHtml(item.company)}</div>
                <ul>
                    ${item.bullets.map(b => `<li>${escapeHtml(b)}</li>`).join('')}
                </ul>
                ${item.tech && item.tech.length ? `<div class="tech-stack"><strong>Tech:</strong> ${escapeHtml(item.tech.join(', '))}</div>` : ''}
            </div>
        `).join('')}
    </div>

    <h2>${escapeHtml(data.education.heading)}</h2>
    <div class="section-content">
        ${data.education.items.map(item => `
            <div class="item-block">
                <div class="item-header">
                    <span class="item-title">${escapeHtml(item.degree)}</span>
                    <span class="item-meta">${escapeHtml(item.period)}</span>
                </div>
                <div class="item-subtitle">${escapeHtml(item.institution)}</div>
                ${item.notes ? `<p>${escapeHtml(item.notes)}</p>` : ''}
            </div>
        `).join('')}
    </div>

    <h2>${escapeHtml(data.certifications.heading)}</h2>
    <div class="section-content">
        <ul>
            ${data.certifications.items.map(item => `
                <li><strong>${escapeHtml(item.name)}</strong> - ${escapeHtml(item.issuer)} (${escapeHtml(item.year)})</li>
            `).join('')}
        </ul>
    </div>

    <h2>${escapeHtml(data.skills.heading)}</h2>
    <div class="section-content skills-grid">
        ${data.skills.groups ? data.skills.groups.map(group => `
            <div class="skill-group">
                <h4>${escapeHtml(group.category)}</h4>
                <p>${escapeHtml(group.items.join(', '))}</p>
            </div>
        `).join('') : ''}
    </div>

    <h2>${escapeHtml(data.projects.heading)}</h2>
    <div class="section-content">
        ${data.projects.items.map(item => `
            <div class="item-block">
                <div class="item-header">
                    <span class="item-title">${escapeHtml(item.name)}</span>
                </div>
                <p>${escapeHtml(item.description)}</p>
            </div>
        `).join('')}
    </div>

</body>
</html>
    `;
}

/**
 * Generates an HTML document that behaves as a print-ready CV when opened.
 * Reads the `content.json` file schema, interpolates safe escaped text and URLs
 * to formulate the layout, and finally triggers a print window on a new blank tab.
 * Prompts the user to allow popups if the tab could not be opened.
 *
 * @returns {void}
 */
export function generateCV() {
    const data = getContent();
    if (!data) {
        alert("Content not loaded yet. Please try again in a moment.");
        return;
    }

    const html = generateCVHtml(data);

    const printWindow = window.open('', '_blank');
    if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
        
        // Use a slight timeout to ensure styles apply before printing
        setTimeout(() => {
            printWindow.print();
        }, 250);
    } else {
        alert("Please allow popups to export your CV.");
    }
}
